import { useContext, useEffect, useMemo } from "react"

import { useEvent } from "shared/lib/useEvent"
import { useTimeout } from "shared/lib/useTimeout"
import { WithID } from "shared/types/WithID"

import { TableContext } from "../../TableContext"
import { useTableItem } from "../../hooks/useTableItem"
import { RowInnerComponentProps, RowViewModel } from "../Row"

export function useRowViewModel<I extends WithID>(
  props: RowInnerComponentProps<I>
): RowViewModel<I> {
  const { item, isNextParent } = props
  const { isItemCollapsed, onItemCollapse, RowRenderer } =
    useContext(TableContext)
  const timeout = useTimeout()

  const isCollapsed = useMemo(
    () => !!isItemCollapsed?.(props.item),
    [isItemCollapsed, props.item]
  )

  const handleCollapse = useEvent(() => {
    onItemCollapse?.(props.item.id)
  })

  useEffect(() => {
    if (isNextParent) {
      timeout.start(1000, () => isCollapsed && handleCollapse())
    } else {
      timeout.clear()
    }
  }, [isNextParent, isCollapsed])

  return {
    item,
    RowRenderer,
    isNextParent,
    isCollapsed,
    onCollapse: handleCollapse,
    ...useTableItem(props)
  }
}
